import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ScreenBox } from '../useEditorCanvas';
import { useWheelToCanvas } from '../wheelToCanvas';

/*
 * The brush for Erase Object and Edit with Words' area: painted straight on the picture, over the
 * canvas, while the tool is open. Strokes are kept in the picture's own 0–1 space, so a pan or zoom
 * keeps them where they were painted, and the mask is drawn at the picture's own size when it is
 * sent — white where it was painted, black elsewhere, which every inpainting model reads.
 */

/** `mask(w, h)`: white on black; with `colour`, the strokes alone in that colour on transparency (to lay over the picture). */
export interface MaskBrushHandle { mask(width: number, height: number, colour?: string): Promise<Blob | null>; clear(): void }
type Stroke = { r: number; points: { x: number; y: number }[] };

export const MaskBrush = forwardRef<MaskBrushHandle, { box: ScreenBox; size: number; onPainted(painted: boolean): void }>(function MaskBrush({ box, size, onPainted }, ref) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const drawing = useRef<Stroke | null>(null);
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null);
  const width = Math.max(1, box.right - box.left), height = Math.max(1, box.bottom - box.top);

  useEffect(() => { onPainted(strokes.length > 0); }, [strokes.length, onPainted]);
  // A scroll or pinch over the brush is the canvas's, so the brush follows the view.
  const layer = useWheelToCanvas<HTMLDivElement>();
  useImperativeHandle(ref, () => ({
    clear: () => setStrokes([]),
    mask: async (w, h, colour) => {
      if (!strokes.length) return null;
      const out = document.createElement('canvas'); out.width = Math.max(1, Math.round(w)); out.height = Math.max(1, Math.round(h));
      const g = out.getContext('2d')!;
      if (!colour) { g.fillStyle = '#000'; g.fillRect(0, 0, out.width, out.height); }
      paint(g, strokes, out.width, out.height, colour ?? '#fff');
      return new Promise(resolve => out.toBlob(blob => resolve(blob), 'image/png'));
    },
  }), [strokes]);

  useLayoutEffect(() => {
    const c = canvas.current; if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.round(width * dpr); c.height = Math.round(height * dpr);
    const g = c.getContext('2d')!;
    g.setTransform(dpr, 0, 0, dpr, 0, 0); g.clearRect(0, 0, width, height);
    paint(g, strokes, width, height, 'rgb(124 92 255 / 0.5)');
  }, [strokes, width, height]);

  const at = (event: React.PointerEvent) => ({ x: (event.clientX - box.left) / width, y: (event.clientY - box.top) / height });
  return createPortal(<div ref={layer} className="artwork-mask-brush" data-keeps-popover="true" style={{ left: box.left, top: box.top, width, height }}
    onPointerDown={event => {
      event.preventDefault(); event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      // The stroke itself, not the ref: the updater can run after a release has cleared it.
      const stroke: Stroke = { r: size / 2 / width, points: [at(event)] };
      drawing.current = stroke;
      setStrokes(previous => [...previous, stroke]);
    }}
    onPointerMove={event => {
      setHover({ x: event.clientX - box.left, y: event.clientY - box.top });
      const stroke = drawing.current;
      if (!stroke) return;
      stroke.points.push(at(event));
      const moved = { ...stroke, points: [...stroke.points] };
      setStrokes(previous => [...previous.slice(0, -1), moved]);
    }}
    onPointerUp={() => { drawing.current = null; }}
    onPointerLeave={() => setHover(null)}>
    <canvas ref={canvas} style={{ width, height }} aria-hidden />
    {hover && <span className="artwork-mask-cursor" style={{ left: hover.x - size / 2, top: hover.y - size / 2, width: size, height: size }} aria-hidden />}
  </div>, document.body);
});

function paint(g: CanvasRenderingContext2D, strokes: readonly Stroke[], width: number, height: number, colour: string) {
  g.strokeStyle = colour; g.fillStyle = colour; g.lineCap = 'round'; g.lineJoin = 'round';
  for (const stroke of strokes) {
    if (!stroke?.points?.length) continue;
    const r = stroke.r * width;
    if (stroke.points.length === 1) { const p = stroke.points[0]!; g.beginPath(); g.arc(p.x * width, p.y * height, r, 0, Math.PI * 2); g.fill(); continue; }
    g.lineWidth = r * 2;
    g.beginPath();
    stroke.points.forEach((p, i) => i ? g.lineTo(p.x * width, p.y * height) : g.moveTo(p.x * width, p.y * height));
    g.stroke();
  }
}
